import React, { useState } from 'react';
import PageHero from './PageHero';
import { Calendar, MapPin, Clock, ArrowRight, Star, Music, Trophy, X } from 'lucide-react';
import event1Img from '../assets/WHY GMIS - HOLISTIC.png';
import event2Img from '../assets/Banner-GMIS Students.png';
import event3Img from '../assets/WHY GMIS-IB,Cambridge.JPG';

const EVENTS = [
  {
    id: 1,
    title: "Annual Sports Meet 2026",
    category: "Sports",
    date: "August 28, 2026",
    time: "08:00 AM - 02:00 PM",
    location: "GMIS Sports Ground",
    desc: "Join us for our Annual Sports Meet, featuring track and field events, team sports, and a spectacular opening ceremony. Students from all grades will compete for the prestigious House Cup.",
    img: event1Img,
    icon: <Trophy size={16} />
  },
  {
    id: 2,
    title: "Indonesian Cultural Week",
    category: "Culture",
    date: "August 12-14, 2026",
    time: "09:00 AM - 03:00 PM",
    location: "Main Auditorium",
    desc: "A three-day celebration of Indonesia's rich heritage. Expect traditional dances, culinary delights, batik workshops, and special assemblies showcasing the diversity of the archipelago.",
    img: event2Img,
    icon: <Star size={16} />
  },
  {
    id: 3,
    title: "Grade 12 Theatre Showcase",
    category: "Arts",
    date: "September 12, 2026",
    time: "06:00 PM - 08:30 PM",
    location: "School Theatre",
    desc: "Our senior students present a dramatic performance entirely produced, directed, and acted by the Grade 12 cohort. A true showcase of creativity and collaboration.",
    img: event3Img,
    icon: <Music size={16} />
  }
];

export default function SchoolEventsPage({ setPage, t }) {
  const [filter, setFilter] = useState('All');
  const [selectedEvent, setSelectedEvent] = useState(null);

  const navCategory = t?.nav?.[4]?.label || "News & Events";
  const homeLabel = t?.nav?.[0]?.label || "Home";

  const categories = ['All', 'Sports', 'Culture', 'Arts', 'Community', 'Special', 'Academic'];

  const filteredEvents = filter === 'All'
    ? EVENTS
    : EVENTS.filter(e => e.category === filter);

  return (
    <div className="w-full bg-[#f8f9fa] min-h-screen pb-32 relative">
      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <PageHero
        title={"School Events"}
        breadcrumbs={[homeLabel, navCategory, "School Events"]}
        eyebrow={navCategory}
        subtitle={"Discover what's happening in and around the GMIS community."}
      />


      {/* ── Filter Bar ──────────────────────────────────────────────────────── */}
      <div className="max-w-[1000px] mx-auto px-6 py-10">
        <div className="flex flex-wrap items-center gap-2">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-all duration-300 ${filter === cat
                  ? 'bg-navy text-white shadow-md'
                  : 'bg-white border border-line text-ink/70 hover:border-navy hover:text-navy'
                }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* ── Events Grid ─────────────────────────────────────────────────────── */}
      <div className="max-w-[1000px] mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {filteredEvents.map(event => (
            <div key={event.id} className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-line flex flex-col">
              <div className="relative h-[160px] overflow-hidden">
                <img
                  src={event.img}
                  alt={event.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute top-3 left-3 bg-white/90 backdrop-blur px-2.5 py-1 rounded-full text-[10px] font-bold text-navy uppercase tracking-widest flex items-center gap-1.5 shadow-sm">
                  {event.icon} {event.category}
                </div>
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <h3 className="text-lg font-heading font-bold text-navy leading-tight mb-3 group-hover:text-gold transition-colors">
                  {event.title}
                </h3>

                <div className="space-y-1.5 mb-4">
                  <div className="flex items-center gap-2 text-xs text-ink/70">
                    <Calendar size={14} className="text-gold shrink-0" />
                    <span>{event.date}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-ink/70">
                    <Clock size={14} className="text-gold shrink-0" />
                    <span>{event.time}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-ink/70">
                    <MapPin size={14} className="text-gold shrink-0" />
                    <span>{event.location}</span>
                  </div>
                </div>

                <p className="text-ink/70 text-sm leading-relaxed mb-5 flex-1 line-clamp-3">
                  {event.desc}
                </p>

                <button
                  onClick={() => setSelectedEvent(event)}
                  className="flex items-center gap-1.5 text-navy font-bold hover:text-gold transition-colors text-xs uppercase tracking-wider mt-auto group/btn"
                >
                  Event Details
                  <ArrowRight size={14} className="transition-transform group-hover/btn:translate-x-1" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {filteredEvents.length === 0 && (
          <div className="text-center py-16 bg-white rounded-2xl border border-line mt-6">
            <Star size={40} className="mx-auto text-ink/20 mb-3" />
            <h3 className="text-lg font-heading font-bold text-navy mb-1">No Events Found</h3>
            <p className="text-sm text-ink/60">We couldn't find any events matching this category.</p>
            <button
              onClick={() => setFilter('All')}
              className="mt-4 text-xs text-gold font-bold hover:underline"
            >
              Clear Filter
            </button>
          </div>
        )}
      </div>

      {/* ── Event Modal ─────────────────────────────────────────────────────── */}
      {selectedEvent && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-navy/80 backdrop-blur-sm"
            onClick={() => setSelectedEvent(null)}
          />
          <div className="relative bg-white w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh] animate-in fade-in zoom-in duration-300">

            <div className="relative h-[250px] shrink-0">
              <img
                src={selectedEvent.img}
                alt={selectedEvent.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/30 to-transparent" />
              <button
                onClick={() => setSelectedEvent(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-black/20 hover:bg-black/40 backdrop-blur text-white rounded-full flex items-center justify-center transition-colors"
              >
                <X size={20} />
              </button>

              <div className="absolute bottom-6 left-8 right-8">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gold text-navy text-[10px] font-bold uppercase tracking-widest mb-3 shadow-sm">
                  {selectedEvent.icon} {selectedEvent.category}
                </div>
                <h2 className="text-3xl font-heading font-bold text-white leading-tight uppercase">
                  {selectedEvent.title}
                </h2>
              </div>
            </div>

            <div className="p-8 overflow-y-auto">
              <div className="flex flex-wrap gap-6 mb-8 border-b border-line pb-8">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center shrink-0">
                    <Calendar size={18} className="text-gold" />
                  </div>
                  <div>
                    <p className="text-[11px] font-bold text-ink/40 uppercase tracking-widest">Date</p>
                    <p className="text-sm font-semibold text-navy">{selectedEvent.date}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center shrink-0">
                    <Clock size={18} className="text-gold" />
                  </div>
                  <div>
                    <p className="text-[11px] font-bold text-ink/40 uppercase tracking-widest">Time</p>
                    <p className="text-sm font-semibold text-navy">{selectedEvent.time}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center shrink-0">
                    <MapPin size={18} className="text-gold" />
                  </div>
                  <div>
                    <p className="text-[11px] font-bold text-ink/40 uppercase tracking-widest">Location</p>
                    <p className="text-sm font-semibold text-navy">{selectedEvent.location}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-heading font-bold text-navy mb-3">About this Event</h3>
                <p className="text-ink/80 text-sm leading-relaxed mb-8">
                  {selectedEvent.desc}
                </p>
              </div>

              <div className="bg-navy/5 rounded-2xl p-6 text-center">
                <h4 className="font-heading font-bold text-navy mb-2">Interested in attending?</h4>
                <p className="text-xs text-ink/60 mb-4">Please note that some events require prior registration.</p>
                <button className="bg-navy text-white px-8 py-3 rounded-full text-sm font-bold tracking-wide hover:bg-gold hover:text-navy transition-colors shadow-lg">
                  Register Interest
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
